import { TOOL_NAMES } from 'js/src/agent/toolNames';
import React from 'react';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { Tool } from './Tool';

import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { ImmutableFromJS } from 'js/src/libs/consts';

interface MediaRepresentation {
    id: string;
    type: string;
    title: string;
    tags: string;
    attractiveness: number | 'UNKNOWN';
    visualDescription?: string;
    decade?: string;
}

function formatDecadeForLLM(decade: string | undefined): string | undefined {
    if (!decade) {
        return undefined;
    }
    const startYear = parseInt(decade, 10);
    if (isNaN(startYear)) {
        return undefined;
    }
    const endYear = startYear + 9;
    return `${startYear}-${endYear}`;
}

export const listMediaSchema = z.object({});
export type ListMediaArgs = z.infer<typeof listMediaSchema>;

export class ListMediaTool extends Tool<typeof listMediaSchema> {
    protected override readonly schema = listMediaSchema;
    protected override readonly toolName = TOOL_NAMES.listAvailableMedia;
    protected override readonly toolDescription =
        'Lists all media assets currently available for use';

    public override customRenderer = ListMediaResultRenderer;

    public override getToolDisplayName(): string {
        return 'Listing available media';
    }

    public get statusIcon(): string {
        return 'perm_media';
    }

    public async execute(args: ListMediaArgs, context: ToolExecutionContext) {
        try {
            const mediaRegistry = MediaRegistry.getInstance();
            const representations = mediaRegistry.getAllRepresentations();

            if (representations.length === 0) {
                return 'No media assets are currently available. Use the search tool to find media.';
            }

            // Group by source category and remove the category field, format decade
            const sourceMedia = representations
                .filter((r) => r.sourceCategory === 'source')
                .map((r) => {
                    const { sourceCategory, decade, ...rest } = r;
                    return {
                        ...rest,
                        ...(decade ? { decade: formatDecadeForLLM(decade) } : {}),
                    };
                });
            const uploadedMedia = representations
                .filter((r) => r.sourceCategory === 'uploads')
                .map((r) => {
                    const { sourceCategory, decade, ...rest } = r;
                    return {
                        ...rest,
                        ...(decade ? { decade: formatDecadeForLLM(decade) } : {}),
                    };
                });
            const stockMedia = representations
                .filter((r) => r.sourceCategory === 'stock')
                .map((r) => {
                    const { sourceCategory, decade, ...rest } = r;
                    return {
                        ...rest,
                        ...(decade ? { decade: formatDecadeForLLM(decade) } : {}),
                    };
                });

            // Return structured JSON with grouping for both LLM and UI renderer
            return JSON.stringify({
                sourceMedia,
                uploadedMedia,
                stockMedia,
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error listing available media: ${errorMessage}`;
        }
    }
}

// Helper function to render media assets
const renderMediaAssets = (
    reps: MediaRepresentation[],
    mediaRegistry: ReturnType<typeof MediaRegistry.getInstance>,
    startIndex: number = 0,
) => {
    return reps.map((rep: MediaRepresentation, index: number) => {
        try {
            const fullData = mediaRegistry.getItem(rep.id);
            if (!fullData) {
                return (
                    <div key={startIndex + index} className="col-md-6">
                        ID: {rep.id} - {rep.type}: {rep.title}
                    </div>
                );
            }

            const immutableAsset = ImmutableFromJS(fullData);
            return (
                <div key={startIndex + index} className="row g-2 mb-l5-8">
                    <BackgroundAsset
                        searchContext={null}
                        media={immutableAsset}
                        media_id={startIndex + index}
                        result_index={startIndex + index}
                    />
                    <div className="col-md-6">
                        ID: {rep.id}
                        <br />
                        {rep.title}
                        <br />
                        {rep.visualDescription && (
                            <>
                                Visual: {rep.visualDescription}
                                <br />
                            </>
                        )}
                        {rep.decade && (
                            <>
                                Decade: {rep.decade}
                                <br />
                            </>
                        )}
                        score: {rep.attractiveness}
                    </div>
                </div>
            );
        } catch (error) {
            return (
                <div key={startIndex + index} className="col-md-6">
                    ID: {rep.id} - {rep.type}: {rep.title}
                </div>
            );
        }
    });
};

const ListMediaResultRenderer: React.FC<CustomRendererComponentProps> = ({ isLoading, result }) => {
    if (isLoading) {
        return null;
    }
    const mediaRegistry = MediaRegistry.getInstance();

    let parsedResult;
    try {
        parsedResult = JSON.parse(result);
    } catch (error) {
        return <div>{result}</div>;
    }

    // Extract grouped media from the result
    const sourceMedia: MediaRepresentation[] = parsedResult.sourceMedia || [];
    const uploadedMedia: MediaRepresentation[] = parsedResult.uploadedMedia || [];
    const stockMedia: MediaRepresentation[] = parsedResult.stockMedia || [];
    const totalCount = sourceMedia.length + uploadedMedia.length + stockMedia.length;

    return (
        <div>
            <div className="mb-l5-12">
                <strong>Found {totalCount} available media assets</strong>
            </div>

            {sourceMedia.length > 0 && (
                <div className="mb-l5-16">
                    <h6>Source Media ({sourceMedia.length})</h6>
                    <small className="text-muted d-block mb-l5-8">
                        From article/document - likely depicts specific subject
                    </small>
                    <div className="asset-container">
                        {renderMediaAssets(sourceMedia.slice(0, 4), mediaRegistry, 0)}
                    </div>
                    {sourceMedia.length > 4 && (
                        <div className="text-muted">
                            ... and {sourceMedia.length - 4} more source media
                        </div>
                    )}
                </div>
            )}

            {uploadedMedia.length > 0 && (
                <div className="mb-l5-16">
                    <h6>Uploaded Media ({uploadedMedia.length})</h6>
                    <small className="text-muted d-block mb-l5-8">
                        User uploads - may have specific meaning
                    </small>
                    <div className="asset-container">
                        {renderMediaAssets(
                            uploadedMedia.slice(0, 4),
                            mediaRegistry,
                            sourceMedia.length,
                        )}
                    </div>
                    {uploadedMedia.length > 4 && (
                        <div className="text-muted">
                            ... and {uploadedMedia.length - 4} more uploaded media
                        </div>
                    )}
                </div>
            )}

            {stockMedia.length > 0 && (
                <div className="mb-l5-16">
                    <h6>Stock Media ({stockMedia.length})</h6>
                    <small className="text-muted d-block mb-l5-8">Generic library content</small>
                    <div className="asset-container">
                        {renderMediaAssets(
                            stockMedia.slice(0, 4),
                            mediaRegistry,
                            sourceMedia.length + uploadedMedia.length,
                        )}
                    </div>
                    {stockMedia.length > 4 && (
                        <div className="text-muted">
                            ... and {stockMedia.length - 4} more stock media
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
